import React, { useMemo, useState } from 'react';

import { MapPin } from 'lucide-react';
import {
  components,
  CSSObjectWithLabel,
  InputActionMeta,
  InputProps,
  OptionProps
} from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';

import { getGooglePlaceDetail, getGooglePlacesResults, PlacePrediction } from 'src/apis/googleAPI';
import { useISIntl } from 'src/i18n';
import BillingModel from 'src/models/BillingModel';
import { SearchDropdownIndicator } from './DropdownIndicator';
import { messages } from './messages';

const Input = (props: InputProps) => <components.Input {...props} isHidden={false} />;

interface AutocompleteDropdownProps {
  label: string;
  placeholder: string;
  disabled?: boolean;
  inputId?: string;
  hideDropdownIndicator?: boolean;
}

const MIN_INPUT_LENGTH = 3;

export const BillingAutoComplete = (props: AutocompleteDropdownProps) => {
  const billingModel = BillingModel.getInstance();
  const { label, placeholder, disabled, inputId, hideDropdownIndicator } = props;
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { locale, f } = useISIntl();
  // controlled input https://github.com/JedWatson/react-select/issues/1826#issuecomment-737463954
  const onInputChange = (value: string, { action }: InputActionMeta) => {
    if (action === 'input-blur' && value) {
      setInputValue(billingModel.prediction?.description ?? '');
    }

    if (action === 'input-change') {
      setInputValue(value);
      // If user types manually, clear prediction and address fields
      billingModel.update(null, null);
    }
  };

  const onChange = async (option: { label: string; value: PlacePrediction } | null) => {
    setError(null);
    if (!option || !option.value) {
      setInputValue('');
      billingModel.update(null, null);
      return;
    }

    // Only allow selection from valid dropdown options (not manually created)
    if (option.value && typeof option.value === 'object' && option.value.place_id) {
      setInputValue(option.label);
      const details = await getGooglePlaceDetail(
        option.value.place_id,
        locale,
        billingModel.googleSessionToken
      );
      billingModel.update(option.value, details);
      if (!billingModel.isValid.validity) {
        setError(billingModel.isValid.error);
        billingModel.reset();
      }
    } else {
      // Reject manual entries - clear input and model
      setInputValue('');
      billingModel.update(null, null);
    }
    billingModel.regenerateGoogleSessionToken();
  };

  const searchOptions = async (inputValue?: string) => {
    setError(null);
    if (!inputValue || inputValue.length < MIN_INPUT_LENGTH) {
      return [];
    }
    const places = await getGooglePlacesResults(
      inputValue,
      locale,
      billingModel.googleSessionToken
    );
    const results = places.reduce((result: PlacePrediction[], currPlace: PlacePrediction) => {
      // checks for 5-digit zip codes, [5 digit][dash][4 digit] zip codes, and Canadian postal codes
      const ZIP_CODE_VALIDATION_REGEX =
        /^(\d{5}-\d{4})|(\d{5})|([a-zA-Z]\d[a-zA-Z]\s?\d[a-zA-Z]\d)$/;
      const terms = currPlace.terms.filter((term) => ZIP_CODE_VALIDATION_REGEX.test(term.value));
      if (terms.length > 0) {
        result.push(currPlace);
      }
      return result;
    }, []);
    if (results.length === 0) {
      setError(f(messages.alertInvalidAddress));
    }
    return results.map((place: PlacePrediction) => ({
      label: place.description,
      value: place
    }));
  };

  const IconOption = (props: OptionProps) => {
    return (
      <components.Option {...props}>
        <MapPin
          fill="var(--tailwind-slate-500)"
          stroke={props.isFocused || props.isSelected ? '#F3F4F6' : 'white'}
        />
        {props.label}
      </components.Option>
    );
  };

  const baseCustomComponents = { Input, Option: IconOption };

  const customComponents = hideDropdownIndicator
    ? {
        ...baseCustomComponents,
        DropdownIndicator: SearchDropdownIndicator,
        IndicatorSeparator: () => null
      }
    : baseCustomComponents;

  // required to remove the inner blue focus outline https://stackoverflow.com/a/76966526/26852697
  const style = useMemo(
    () => ({
      control: (baseStyles: CSSObjectWithLabel, state) => ({
        ...baseStyles,
        border: state.isFocused ? '1px solid #80bdff' : '1px solid #aaa',
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none'
      }),
      input: (baseStyles: CSSObjectWithLabel) => ({
        ...baseStyles,
        'input:focus': {
          boxShadow: 'none',
          border: 'none'
        }
      }),
      option: (baseStyles: CSSObjectWithLabel, state) => {
        return {
          ...baseStyles,
          backgroundColor: state.isSelected || state.isFocused ? '#F3F4F6' : undefined,
          ':hover': {
            backgroundColor: '#F3F4F6'
          }
        };
      }
    }),
    []
  );

  const formatValue = () => {
    if (inputValue && billingModel?.prediction) {
      return { label: billingModel.prediction.description, value: billingModel.prediction };
    }
    return null;
  };

  return (
    <div className="form-group">
      <label className="pb-1" htmlFor={inputId}>
        {label}
      </label>

      <AsyncCreatableSelect
        components={customComponents}
        styles={style}
        isSearchable
        placeholder={placeholder}
        value={formatValue()}
        inputValue={inputValue}
        classNamePrefix="billing-autocomplete"
        onInputChange={onInputChange}
        onChange={onChange}
        isDisabled={disabled}
        inputId={inputId}
        loadOptions={searchOptions}
        isValidNewOption={() => false}
        allowCreateWhileLoading={false}
        createOptionPosition="first"
      />
      <div
        id="tailwind"
        style={{
          minHeight: '1.5rem',
          height: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start'
        }}>
        {error && <span className="text-base text-red-600">{error}</span>}
      </div>
    </div>
  );
};
